import { useEffect } from 'react';

import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { useAppSelector } from 'hooks';
import {
  fetchGroups,
  fetchTagSVGs,
  fetchTagLabels,
  fetchGroupAdmins,
  fetchTimeLogTasks,
  fetchTimeLogTypes,
  fetchGroupCarePrograms,
  fetchObservationCollections,
  fetchGroupAvatar,
  fetchQuestionnaires,
  fetchDeviceOrderings
} from 'store/groups/service';
import {
  setSelectedGid,
  selectGroups,
  clearAvatar,
  getAuthorizedOrgs,
  getAuthorizedSubgroups
} from 'store/groups/reducer';
import { getUserRoles } from 'store/oauth/reducer';
import { fetchInterventions } from 'store/tasks/service';
import { AccessRoles } from 'constants/enums';

interface UseGroupsDataFetchingOptions {
  accessToken?: string;
  specifiedGid?: string;
}

const useGroupsDataFetching = ({ accessToken, specifiedGid }: UseGroupsDataFetchingOptions) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isSysop = useAppSelector((state) => getUserRoles(state)?.includes(AccessRoles.SYSOP));

  const groupsData = useAppSelector(selectGroups);
  const orgs = useAppSelector(getAuthorizedOrgs);
  const subgroups = useAppSelector(getAuthorizedSubgroups);

  const groupsLoading = useAppSelector((state) => state.groups.groupInfo.loading);

  const groupsError = useAppSelector((state) => state.groups.groupInfo.error);

  useEffect(() => {
    if (accessToken && !groupsData.length && !groupsLoading && !groupsError) {
      dispatch(fetchGroups({ isSysop }));
      dispatch(fetchTagSVGs());
      dispatch(fetchTagLabels());
      dispatch(fetchGroupAdmins());
      dispatch(fetchTimeLogTasks());
      dispatch(fetchTimeLogTypes());
      dispatch(fetchGroupCarePrograms());
      dispatch(fetchObservationCollections());
    }
  }, [accessToken, groupsData.length, groupsLoading, groupsError, specifiedGid, isSysop, dispatch]);

  useEffect(() => {
    if (groupsData.length && specifiedGid) {
      const groupExists = groupsData.some((group) => group.gid === specifiedGid);
      if (!groupExists) {
        return;
      }

      dispatch(setSelectedGid(specifiedGid));
      if (specifiedGid !== 'all') {
        dispatch(fetchGroupAvatar(specifiedGid));
        dispatch(fetchQuestionnaires({ gid: specifiedGid, scope: 'all' }));
        dispatch(fetchDeviceOrderings(specifiedGid));
        const selectedGroup = groupsData.find((group) => group.gid === specifiedGid);
        if (selectedGroup?.parentGid) {
          dispatch(fetchInterventions(selectedGroup.parentGid));
        }
      } else {
        dispatch(clearAvatar());
        // Skip bulk intervention loading for sysop — they'll be redirected to a specific group
        if (!isSysop) {
          const uniqueOrgGids = [
            ...new Set(groupsData.map((group) => group.parentGid).filter(Boolean))
          ];
          uniqueOrgGids.forEach((orgGid) => {
            if (orgGid && orgGid !== 'all') {
              dispatch(fetchInterventions(orgGid));
            }
          });
        }
      }
    }
  }, [
    accessToken,
    specifiedGid,
    groupsData,
    orgs,
    subgroups,
    isSysop,
    dispatch,
    navigate,
    location.pathname
  ]);

  return {
    groupsData,
    groupsLoading,
    groupsError
  };
};

export default useGroupsDataFetching;
